// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AgeVerificationCommands from '@app/features/age_verification/commands/AgeVerificationCommands';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {isAgeVerificationAvailableInRegion} from '@app/features/moderation/utils/NSFWGeoUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useRef, useState} from 'react';
export const AgeVerificationModal = observer(() => {
	const {t} = useLingui();
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const initialFocusRef = useRef<HTMLButtonElement | null>(null);
	const ageVerificationAvailable = isAgeVerificationAvailableInRegion();
	const handleVerify = useCallback(async () => {
		setSubmitting(true);
		setError(null);
		try {
			const url = await AgeVerificationCommands.createVerificationSession();
			ModalCommands.pop();
			await openExternalUrl(url);
		} catch {
			setError(t`Something went wrong. Please try again.`);
		} finally {
			setSubmitting(false);
		}
	}, [t]);
	const handleCancel = useCallback(() => {
		ModalCommands.pop();
	}, []);
	return (
		<Modal.Root size="small" initialFocusRef={initialFocusRef} centered>
			<Modal.Header title={t`Verify Your Age`} />
			<Modal.Content>
				<Modal.ContentLayout>
					{ageVerificationAvailable ? (
						<>
							<Modal.Description>
								<Trans>
									UK law requires age verification for adult content. To verify your age, you'll complete a $0.00 credit
									card authorization through Stripe. Your card will not be charged.
								</Trans>
							</Modal.Description>
							<Modal.Description>
								<Trans>
									Only credit cards are accepted. Debit and prepaid cards cannot be used for age verification.
								</Trans>
							</Modal.Description>
						</>
					) : (
						<Modal.Description>
							<Trans>Age verification is currently available only in the UK.</Trans>
						</Modal.Description>
					)}
					{error != null && <Modal.Description>{error}</Modal.Description>}
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				{ageVerificationAvailable ? (
					<Button onClick={handleCancel} variant="secondary">
						<Trans>Cancel</Trans>
					</Button>
				) : (
					<Button onClick={handleCancel} variant="primary">
						<Trans>Close</Trans>
					</Button>
				)}
				{ageVerificationAvailable && (
					<Button onClick={handleVerify} submitting={submitting} variant="primary" ref={initialFocusRef}>
						<Trans>Verify with Credit Card</Trans>
					</Button>
				)}
			</Modal.Footer>
		</Modal.Root>
	);
});
